import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { GOAL_STATUSES } from '@ehr/shared';
import {
  NOT_ADDRESSED,
  defaultStatusDate,
  statusCaption,
  statusDateError,
  statusDateLabel,
  statusDated,
  statusLabel,
  statusRangeText,
  statusTone,
  type RowStatus,
  type StatusRange,
} from '../lib/goalStatus.js';
import { DateField } from './DateTimeField.js';

/**
 * ehr#571 (PROGRESS_NOTE_10-20-V141, STATUS_PICKER_A): a goal's or objective's status this session.
 * One button per row; it opens "How did it go this session?" with the seven statuses (the plan's
 * own status tagged), and a pick is saved at once. The menu then turns into "Saved as ...": an
 * optional note and the status date, limited to the plan's signing day .. today (Chase 10-21,
 * ask 1), with Skip and Done. The note itself shows below the row (StatusNote, ask 2).
 */
export type StatusView = 'list' | 'note';

const CHECK = (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden fill="none" stroke="currentColor">
    <path d="M5 12.5l4.5 4.5L19 7.5" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);
const CHEV = (
  <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden fill="none" stroke="currentColor">
    <path d="M6 9l6 6 6-6" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Dot = ({ status }: { status?: string }) => (
  <i className={`gs-dot ${statusTone(status)}${status === 'Partially met' ? ' half' : ''}`} />
);

export function GoalStatusCell({
  text,
  value,
  onChange,
  open,
  onOpen,
  range,
  sessionDate,
  locked,
}: {
  /** The goal or objective, for the button's and menu's spoken names. */
  text: string;
  value: RowStatus;
  onChange: (next: RowStatus) => void;
  /** Which view of the menu is open (the parent keeps one open at a time). */
  open: StatusView | false;
  onOpen: (view: StatusView | false) => void;
  range: StatusRange;
  sessionDate: string;
  locked?: boolean;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [noteText, setNoteText] = useState(value.note ?? '');
  const [dateText, setDateText] = useState(value.statusDate ?? '');
  const [dateErr, setDateErr] = useState<string | null>(null);
  const status = value.status;
  const keep = useRef<() => void>(() => {});
  // The menu opens below its button, or above it when the sign-off bar would cover it (V81).
  const [up, setUp] = useState(false);
  useLayoutEffect(() => {
    if (!open) return setUp(false);
    const btn = ref.current?.querySelector('.gs-btn');
    const pop = ref.current?.querySelector<HTMLElement>('.gs-pop');
    if (!btn || !pop) return;
    const r = btn.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - 90; // the sign-off bar
    setUp(pop.offsetHeight > below && r.top > below);
  }, [open]);

  // The note view starts from what is saved each time it opens.
  useEffect(() => {
    if (open !== 'note') return;
    setNoteText(value.note ?? '');
    setDateText(value.statusDate ?? '');
    setDateErr(null);
    // only when the view opens or the status changes
  }, [open, value.status]);

  // Leaving the menu (a click elsewhere, Esc) keeps a typed note (V91), never a bad date.
  keep.current = () => {
    if (open !== 'note' || !status) return;
    const n = noteText.trim();
    const d =
      statusDated(status) && !statusDateError(dateText, range) ? dateText : value.statusDate;
    if (n !== (value.note ?? '') || d !== value.statusDate)
      onChange({ ...value, note: n || undefined, statusDate: d });
  };
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      const t = e.target as Element | null;
      if (!t || ref.current?.contains(t) || t.closest('.dtf-panel')) return;
      keep.current();
      onOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open, onOpen]);
  // The menu takes focus on the status it would keep: the current one, the plan's, or the first.
  useEffect(() => {
    if (open !== 'list') return;
    const el = ref.current;
    const pick =
      el?.querySelector<HTMLElement>('.gs-opt[aria-checked="true"]') ??
      el?.querySelector<HTMLElement>('.gs-opt.plan') ??
      el?.querySelector<HTMLElement>('.gs-opt');
    pick?.focus({ preventScroll: true });
  }, [open]);

  const close = (focusBack = true) => {
    onOpen(false);
    if (focusBack)
      window.requestAnimationFrame(() =>
        ref.current?.querySelector<HTMLElement>('.gs-btn')?.focus({ preventScroll: true }),
      );
  };
  const pick = (s: string) => {
    if (s === status) return close();
    onChange({
      ...value,
      status: s as RowStatus['status'],
      statusDate: defaultStatusDate({
        status: s,
        sessionDate,
        planStatus: value.planStatus,
        range,
      }),
    });
    onOpen('note');
  };
  const done = () => {
    if (statusDated(status)) {
      const e = dateText ? statusDateError(dateText, range) : 'Pick the date.';
      if (e) return setDateErr(e);
    }
    const n = noteText.trim();
    onChange({
      ...value,
      note: n || undefined,
      statusDate: statusDated(status) ? dateText : undefined,
    });
    close();
  };
  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      keep.current();
      close();
      return;
    }
    if (open !== 'list' || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')) return;
    e.preventDefault();
    const opts = [...(ref.current?.querySelectorAll<HTMLElement>('.gs-opt') ?? [])];
    const i = opts.indexOf(document.activeElement as HTMLElement);
    const n = e.key === 'ArrowDown' ? i + 1 : i - 1;
    opts[(n + opts.length) % opts.length]?.focus();
  };

  const cap = statusCaption(status, value.planStatus);
  let btn;
  if (status)
    btn = (
      <button
        type="button"
        className={`gs-btn set ${statusTone(status)}`}
        disabled={locked}
        aria-haspopup="menu"
        aria-expanded={open === 'list'}
        aria-label={`Status for ${text}: ${statusLabel(status, value.statusDate)}`}
        onClick={() => onOpen(open ? false : 'list')}
      >
        <Dot status={status} />
        <span>{statusLabel(status, value.statusDate)}</span>
        {!locked && CHEV}
      </button>
    );
  else if (locked) btn = null;
  else
    btn = (
      <button
        type="button"
        className="gs-btn pick"
        aria-haspopup="menu"
        aria-expanded={open === 'list'}
        aria-label={`Status for ${text}: ${value.planStatus ? `plan status ${value.planStatus}` : 'set status'}`}
        onClick={() => onOpen(open ? false : 'list')}
      >
        {value.planStatus ? (
          <>
            <span className="gs-plan">Plan</span>
            <span>{value.planStatus}</span>
          </>
        ) : (
          <span>Set status</span>
        )}
        {CHEV}
      </button>
    );

  return (
    <span className="gs-cell" ref={ref}>
      {btn}
      {status && cap && (
        <span className="gs-cap" data-test="status-cap">
          {cap.text}
          {cap.from && <b> {cap.from}</b>}
        </span>
      )}
      {!status && locked && <span className="gs-cap none">No status this session</span>}
      {open && !locked && (
        <div
          className={`gs-pop${up ? ' up' : ''}`}
          role={open === 'list' ? 'menu' : 'dialog'}
          aria-label={`${open === 'list' ? 'Status' : 'Notes'} for ${text}`}
          onKeyDown={onMenuKey}
        >
          {open === 'list' ? (
            <>
              <div className="gs-pop-h">How did it go this session?</div>
              {GOAL_STATUSES.map((s) => (
                <span key={s}>
                  {s === NOT_ADDRESSED && <span className="gs-sep" />}
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={s === status}
                    className={`gs-opt${s === value.planStatus ? ' plan' : ''}`}
                    onClick={() => pick(s)}
                  >
                    <Dot status={s} />
                    <span>{s}</span>
                    {s === status ? (
                      <span className="gs-ck">{CHECK}</span>
                    ) : s === value.planStatus ? (
                      <span className="gs-tag">{status ? 'Plan' : 'Same as plan'}</span>
                    ) : null}
                  </button>
                </span>
              ))}
              {status && (
                <div className="gs-pop-f">
                  <button
                    type="button"
                    role="menuitem"
                    className="pn-link quiet"
                    onClick={() => {
                      onChange({
                        ...value,
                        status: undefined,
                        statusDate: undefined,
                        note: undefined,
                      });
                      close();
                    }}
                  >
                    Clear status
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="gs-note">
              <div className="gs-ok">
                {CHECK}Saved as {statusLabel(status, value.statusDate)}
              </div>
              <label className="gs-f">
                <span>
                  Notes <i>(optional)</i>
                </span>
                <textarea
                  rows={3}
                  value={noteText}
                  maxLength={2000}
                  autoFocus
                  onChange={(e) => setNoteText(e.target.value)}
                />
              </label>
              {statusDated(status) && (
                <div className="gs-f">
                  <span>{statusDateLabel(status)}</span>
                  <DateField
                    value={dateText}
                    min={range.min || undefined}
                    max={range.max}
                    ariaLabel={statusDateLabel(status)}
                    onChange={(v) => {
                      setDateText(v);
                      setDateErr(null);
                    }}
                  />
                  <span
                    className={`gs-help${dateErr ? ' err' : ''}`}
                    role={dateErr ? 'alert' : undefined}
                  >
                    {dateErr ?? statusRangeText(range)}
                  </span>
                </div>
              )}
              <div className="gs-btns">
                <button type="button" className="btn" onClick={() => close()}>
                  Skip
                </button>
                <button type="button" className="btn primary" onClick={done}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </span>
  );
}

/** V93 (Chase 10-21, ask 2): the note in a green box right below its goal or objective row. */
export function StatusNote({
  text,
  value,
  locked,
  onEdit,
}: {
  text: string;
  value: RowStatus;
  locked?: boolean;
  onEdit: () => void;
}) {
  if (!value.status) return null;
  if (value.note)
    return (
      <div className="gs-nb" data-test="goal-note">
        <span className="gs-nb-main">
          <span className="gs-nb-label">Note</span>
          <span className="gs-nb-text">{value.note}</span>
        </span>
        {!locked && (
          <button
            type="button"
            className="pn-link"
            aria-label={`Edit note for ${text}`}
            onClick={onEdit}
          >
            Edit note
          </button>
        )}
      </div>
    );
  if (locked) return null;
  return (
    <button
      type="button"
      className="pn-link gs-nb-add"
      aria-label={`Add note for ${text}`}
      onClick={onEdit}
    >
      + Add note
    </button>
  );
}
